import { useState } from 'react';
import {
  useStore, useAssignments, useCanEdit, useChannels, usePresenters,
} from '../state/store';
import { api } from '../lib/api';
import ConfirmDialog from './ConfirmDialog';

/**
 * Scheduling operations, shared by the board and the timeline.
 *
 * Both views need these: the board is where mics get placed by hand, and the
 * timeline is where the schedule is read. Having them in one place only meant
 * leaving whichever view you were working in to do the other half of the job.
 */
export default function BoardActions({ compact }: { compact?: boolean }) {
  const identity = useStore(s => s.identity)!;
  const notify = useStore(s => s.notify);
  const session = useStore(s => s.state?.session);
  const presenters = usePresenters();
  const assignments = useAssignments();
  const canEdit = useCanEdit();
  const [confirmClear, setConfirmClear] = useState(false);

  const channels = useChannels();
  /*
   * Nothing to act on means nothing to press. On a show that has only just
   * been created these were live buttons that could only ever answer "nothing
   * to auto-assign" -- a dead click and a toast where a greyed control would
   * have said the same thing without being touched.
   */
  const canPlan = presenters.length > 0 && channels.length > 0;
  const pending = assignments.filter(a => !a.deployed).length;
  const autoDeploy = session?.settings.autoDeploy ?? false;
  const undo = useStore(s => s.state?.undo);

  const run = async (fn: () => Promise<unknown>) => {
    try { await fn(); } catch (e) { notify((e as Error).message, 'error'); }
  };

  /*
   * A viewer gets none of this rather than a row of dead buttons. They are
   * here to read the board -- often a stage manager or a deputy watching on a
   * second screen -- and every control they can never press is width taken
   * from the thing they are actually looking at.
   */
  if (!canEdit) return null;

  return (
    <>
      <button className="btn sm" disabled={!canPlan}
              title={canPlan
                ? 'Place everyone with on-stage times onto a free channel'
                : 'Add presenters and channels first'}
              onClick={() => run(async () => {
                const r = await api.autoAssign(identity.code);
                if (r.placed === 0 && r.unplaced.length === 0) {
                  const noTimes = presenters.length > 0
                    && presenters.every(p => p.slots.length === 0);
                  notify(
                    noTimes
                      ? 'Nothing to auto-assign: no presenter has on-stage times yet. '
                        + 'Add them under Edit → Time slots, or place people by hand.'
                      : 'Nothing to auto-assign.',
                    'error',
                  );
                } else if (r.unplaced.length) {
                  /*
                   * The server works out exactly why each person could not be
                   * placed -- "needs a lav channel, but none exist" -- and this
                   * was throwing all of it away to say "6 could not be placed",
                   * leaving the operator to guess. One reason is usually the
                   * reason, so lead with it.
                   */
                  const why = r.conflicts?.[0]?.message;
                  notify(
                    `Placed ${r.placed}. ${r.unplaced.length} could not be placed.`
                    + (why ? ` ${why}` : ''),
                    'error',
                  );
                } else {
                  notify(`Auto-assigned all ${r.placed}.`);
                }
              })}>
        Auto-assign
      </button>

      {/*
        * Auto-deploy changes what every other action means -- with it on,
        * placing someone puts them live immediately. It used to be buried in
        * settings, so you could be deploying live without knowing.
        */}
      <button className={`btn sm auto-deploy${autoDeploy ? ' on' : ''}`}
              aria-pressed={autoDeploy}
              title={autoDeploy
                ? 'Changes go live the moment you make them. Click to stage them instead.'
                : 'Changes are staged until you deploy. Click to push them live automatically.'}
              onClick={() => run(() =>
                api.updateSettings(identity.code, { autoDeploy: !autoDeploy }))}>
        Auto-deploy {autoDeploy ? 'ON' : 'OFF'}
      </button>

      <button className={`btn sm ${pending > 0 ? 'primary pending' : ''}`}
              disabled={assignments.length === 0}
              title={pending > 0
                ? `${pending} change${pending > 1 ? 's' : ''} not yet live`
                : 'Everything on the board is live'}
              onClick={() => run(async () => {
                await api.deploy(identity.code);
                notify('Deployed.');
              })}>
        Deploy{pending > 0 && <> <span className="pending-count">{pending}</span></>}
      </button>

      {/*
        * Undo. Mid-show a mic disappears off the plot because of a drag that
        * landed one card over, and rebuilding it from memory is the last thing
        * anyone has time for. The label names the change so you can tell
        * whether it is the one you meant to take back.
        */}
      <button className="btn sm" disabled={!undo}
              title={undo
                ? `Undo: ${undo.label}${undo.actor ? `, by ${undo.actor}` : ''}`
                : 'Nothing to undo'}
              onClick={() => run(async () => {
                const r = await api.undo(identity.code);
                notify(`Undid: ${r.undone.label}.`);
              })}>
        Undo
      </button>

      {!compact && (
        <button className="btn sm" disabled={assignments.length === 0}
                title="Take everyone off the board and start again"
                onClick={() => setConfirmClear(true)}>
          Clear
        </button>
      )}

      {confirmClear && (
        <ConfirmDialog
          title="Take everyone off the board?"
          body={`${assignments.length} assignment${assignments.length > 1 ? 's' : ''} will be removed. `
            + 'Your presenters and channels stay exactly as they are.'}
          confirmLabel="Clear the board"
          danger
          onConfirm={() => {
            setConfirmClear(false);
            void run(async () => {
              const r = await api.clearAssignments(identity.code);
              notify(`Cleared ${r.cleared} assignment${r.cleared === 1 ? '' : 's'}.`);
            });
          }}
          onCancel={() => setConfirmClear(false)}
        />
      )}
    </>
  );
}
